import { Game } from "../../core/Game";
import { dueScene, scenePlayed, StorySceneId } from "../../game/story";
import { projectTutorialFacts } from "../projection";
import { StoryTracker } from "../singletons/StoryTracker";

/**
 * The story's command surface: beginning the scene that is owed and
 * finishing the one playing. The script itself, and which scene is
 * owed, is `src/game/story.ts`; the performance between these two
 * calls is the shell's (CutsceneDirector), or nobody's in the headless
 * driver, where a test begins and finishes a scene in one breath.
 *
 * A refusal logs and returns false, like every other command.
 */

/**
 * Whether a scene is up right now — the keys are the story's, the clock
 * is stopped, and she may be walking. The input entities ask this.
 */
export function scenePlaying(game: Game): boolean {
  return game.entities.tryGetSingleton(StoryTracker)?.playing != null;
}

/**
 * Put the owed scene up. Refused while one is already playing, or when
 * nothing is owed — the caller asks `dueScene` first, and this asks
 * again, so a scene can never begin against the script's order.
 */
export function beginScene(game: Game, id: StorySceneId): boolean {
  const tracker = game.entities.getSingleton(StoryTracker);
  if (tracker.playing !== null) {
    console.warn(`A scene is already playing: ${tracker.playing}`);
    return false;
  }
  const owed = dueScene(projectTutorialFacts(game));
  if (owed?.id !== id) {
    console.warn(`Scene ${id} is not owed right now`);
    return false;
  }
  tracker.playing = id;
  game.dispatch("storyChanged", {});
  return true;
}

/**
 * The playing scene has run its last beat: record it played, one-way,
 * and hand the world back. The tutorial card's reveals read the
 * record, so this announces progression too.
 */
export function finishScene(game: Game): boolean {
  const tracker = game.entities.getSingleton(StoryTracker);
  const id = tracker.playing;
  if (id === null) {
    console.warn("No scene is playing");
    return false;
  }
  tracker.playing = null;
  if (!scenePlayed(tracker.progress, id)) {
    tracker.scenesPlayed = [...tracker.scenesPlayed, id];
  }
  game.dispatch("storyChanged", {});
  game.dispatch("progressionChanged", {});
  return true;
}
